import React, { useState } from "react";
import { PrimaryBlueButton, SecondaryBlueButton, IconTrash, Severity } from "@velaro/velaro-component-lib";
import { CreateScraperJobRequest } from "./types";
import useToastAlert from "@/hooks/useToastAlert";

interface CreateScraperJobModalProps {
  onClose: () => void;
  onCreate: (request: CreateScraperJobRequest) => void;
}

export default function CreateScraperJobModal({
  onClose,
  onCreate
}: CreateScraperJobModalProps) {
  const [name, setName] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [urls, setUrls] = useState<string[]>([]);
  const [crawlDepth, setCrawlDepth] = useState(2);
  const [maxPages, setMaxPages] = useState(50);
  const [includeExternal, setIncludeExternal] = useState(false);
  const [crawlResultDelay, setCrawlResultDelay] = useState(1);
  const [language, setLanguage] = useState("english");
  const [excludeNavigationUrls, setExcludeNavigationUrls] = useState(true);
  const [crawlUrlExclusion, setCrawlUrlExclusion] = useState("");
  const [smartScan, setSmartScan] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const toastAlert = useToastAlert();

  const VIDEO_DOMAINS = ["youtube.com", "youtu.be", "vimeo.com", "dailymotion.com", "twitch.tv", "tiktok.com"];

  const isVideoUrl = (url: string) => {
    const lower = url.toLowerCase();
    return VIDEO_DOMAINS.some(domain => lower.includes(domain));
  };

  const addUrl = () => {
    if (!urlInput.trim()) return;

    // Basic URL validation
    try {
      new URL(urlInput);
    } catch {
      toastAlert.displayToast(Severity.Error, "Please enter a valid URL");
      return;
    }

    if (urls.includes(urlInput)) {
      toastAlert.displayToast(Severity.Warning, "URL already in list");
      return;
    }

    if (isVideoUrl(urlInput)) {
      toastAlert.displayToast(
        Severity.Warning,
        "This looks like a video URL. To extract video transcripts/captions, use the Video Training tab instead."
      );
    }

    setUrls([...urls, urlInput]);
    setUrlInput("");
  };

  const removeUrl = (url: string) => {
    setUrls(urls.filter(u => u !== url));
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toastAlert.displayToast(Severity.Error, "Please enter a job name");
      return;
    }
    if (urls.length === 0) {
      toastAlert.displayToast(Severity.Error, "Please add at least one URL");
      return;
    }

    const request: CreateScraperJobRequest = {
      name: name.trim(),
      urls,
      crawlDepth,
      maxPages,
      includeExternal,
      crawlResultDelay,
      language,
      excludeNavigationUrls,
      crawlUrlExclusion: crawlUrlExclusion.trim() || undefined,
      smartScan
    };

    onCreate(request);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Create New Scraper Job</h2>
          <p className="text-sm text-slate-500">Configure web scraping settings</p>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Job Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Job Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Company Website Scrape"
              className="w-full p-2 border rounded-md text-sm"
            />
          </div>

          {/* URLs */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              URLs to Scrape *
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addUrl()}
                placeholder="https://example.com"
                className="flex-1 p-2 border rounded-md text-sm"
              />
              <SecondaryBlueButton label="Add" onClick={addUrl} />
            </div>
            {urls.length > 0 && (
              <div className="mt-2 space-y-1">
                {urls.map((url) => (
                  <div
                    key={url}
                    className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                  >
                    <span className="truncate">{url}</span>
                    <button
                      onClick={() => removeUrl(url)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <IconTrash className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Basic Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Crawl Depth
              </label>
              <select
                value={crawlDepth}
                onChange={(e) => setCrawlDepth(Number(e.target.value))}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value={1}>1 - Start page only</option>
                <option value={2}>2 - Links from start page</option>
                <option value={3}>3 - Two levels deep</option>
                <option value={4}>4 - Three levels deep</option>
                <option value={5}>5 - Four levels deep</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Max Pages
              </label>
              <select
                value={maxPages}
                onChange={(e) => setMaxPages(Number(e.target.value))}
                className="w-full p-2 border rounded-md text-sm"
              >
                <option value={25}>25 pages</option>
                <option value={50}>50 pages</option>
                <option value={100}>100 pages</option>
                <option value={200}>200 pages</option>
                <option value={500}>500 pages</option>
              </select>
            </div>
          </div>

          {/* Language Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Language Filter
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full p-2 border rounded-md text-sm"
            >
              <option value="english">English</option>
              <option value="spanish">Spanish</option>
              <option value="french">French</option>
              <option value="">No filter</option>
            </select>
          </div>

          {/* Checkboxes */}
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={excludeNavigationUrls}
                onChange={(e) => setExcludeNavigationUrls(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Exclude navigation URLs (login, cart, search, etc.)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeExternal}
                onChange={(e) => setIncludeExternal(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Include external links</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={smartScan}
                onChange={(e) => setSmartScan(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">
                Smart Scan — skip pages that haven't changed since the last run
                <span className="ml-1 text-slate-400">(faster, saves quota)</span>
              </span>
            </label>
          </div>

          {/* Advanced Settings Toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showAdvanced ? "Hide" : "Show"} Advanced Settings
          </button>

          {/* Advanced Settings */}
          {showAdvanced && (
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Delay Between Requests (seconds)
                </label>
                <input
                  type="number"
                  value={crawlResultDelay}
                  onChange={(e) => setCrawlResultDelay(Number(e.target.value))}
                  min={0}
                  max={10}
                  className="w-full p-2 border rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  URL Exclusion Patterns (comma-separated)
                </label>
                <input
                  type="text"
                  value={crawlUrlExclusion}
                  onChange={(e) => setCrawlUrlExclusion(e.target.value)}
                  placeholder="/admin, /private, .pdf"
                  className="w-full p-2 border rounded-md text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">
                  URLs containing these patterns will be skipped
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2">
          <SecondaryBlueButton label="Cancel" onClick={onClose} />
          <PrimaryBlueButton label="Create Job" onClick={handleSubmit} />
        </div>
      </div>
    </div>
  );
}
